import React, { useCallback } from 'react'
import AsyncSelect from 'react-select/async'
import { api } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'

// Simple competition type for filter selects
export interface CompetitionFilter {
  tc_id: string
  name: string
}

// Option type extends CompetitionFilter with react-select required fields
interface CompetitionOption extends CompetitionFilter {
  value: string
  label: string
}

export interface CompetitionSelectProps {
  value: CompetitionFilter | CompetitionFilter[] | null
  onChange: (competition: CompetitionFilter | CompetitionFilter[] | null) => void
  isMulti?: boolean
  isClearable?: boolean
  placeholder?: string
  className?: string
  isDisabled?: boolean
}

// Convert CompetitionFilter to react-select option (exported for use elsewhere)
export const toOption = (c: CompetitionFilter): CompetitionOption => ({
  ...c,
  value: c.tc_id,
  label: c.name
})

/**
 * Reusable competition select component with async loading
 * Supports both single and multi-select modes
 * Uses full objects (like CompetitorSelect) for cleaner state management
 */
const CompetitionSelect: React.FC<CompetitionSelectProps> = ({
  value,
  onChange,
  isMulti = false,
  isClearable = true,
  placeholder = 'Search competitions...',
  className = '',
  isDisabled = false
}) => {
  const fetchCompetitionsData = useCallback(async (inputValue: string): Promise<CompetitionOption[]> => {
    if (inputValue.length < 2) return []

    try {
      const response = await api.get('/competitions', { name: inputValue, limit: 50 })
      const competitions = response?.competitions || []

      // Transform API response to CompetitionFilter format
      const filters: CompetitionFilter[] = competitions.map((competition: any) => ({
        tc_id: competition.tc_competition_parent_id,
        name: competition.tc_name || competition.ext_name || 'Unknown Competition'
      }))

      return filters.map(toOption)
    } catch (error) {
      console.error('Failed to load competitions:', error)
      return []
    }
  }, [])

  const loadCompetitions = useDebouncedCallback(fetchCompetitionsData, 300)

  // Convert value to option format
  const selectValue = value
    ? (isMulti ? (value as CompetitionFilter[]).map(toOption) : toOption(value as CompetitionFilter))
    : null

  return (
    <AsyncSelect<CompetitionOption, typeof isMulti>
      value={selectValue}
      onChange={onChange as any}
      loadOptions={loadCompetitions}
      isMulti={isMulti}
      isClearable={isClearable}
      placeholder={placeholder}
      className={`${selectStyles.reactSelect} ${className}`}
      classNamePrefix="react-select"
      isDisabled={isDisabled}
      cacheOptions
      getOptionValue={(option) => option.tc_id}
      getOptionLabel={(option) => option.name}
      loadingMessage={() => "Loading competitions..."}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length < 2
          ? placeholder
          : "No competitions found"
      }
    />
  )
}

export default CompetitionSelect
